import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  FiCheckCircle, FiXCircle, FiPlay, FiLoader,
  FiChevronRight, FiCode, FiList, FiAward,
} from "react-icons/fi"

import { submitInCourseQuiz } from "../../../services/operations/courseDetailsAPI"
import { updateCompletedLectures } from "../../../slices/viewCourseSlice"

// ── JavaScript runner ─────────────────────────────────────────────────────────

async function runJS(code, inputText) {
  const lines = []
  const origLog   = console.log
  const origError = console.error
  console.log   = (...a) => lines.push(a.map(String).join(" "))
  console.error = (...a) => lines.push(a.map(String).join(" "))
  try {
    const injected = `
const __inputLines = ${JSON.stringify((inputText || "").split("\n"))};
let __lineIdx = 0;
function readline() { return __inputLines[__lineIdx++] ?? ""; }
const input = ${JSON.stringify(inputText || "")};
${code}
`
    await new Function("return (async () => { " + injected + " })()")()
  } catch (e) {
    lines.push("Error: " + e.message)
  } finally {
    console.log   = origLog
    console.error = origError
  }
  return lines.join("\n")
}

// ── Python runner (Pyodide) ───────────────────────────────────────────────────

async function getPyodide() {
  if (window.__kv_pyodide) return window.__kv_pyodide
  const pyodide = await window.loadPyodide({ indexURL: "https://cdn.jsdelivr.net/pyodide/v0.26.4/full/" })
  window.__kv_pyodide = pyodide
  return pyodide
}

async function runPython(code, inputText) {
  try {
    const py = await getPyodide()
    const wrapped = `
import sys, io
sys.stdin = io.StringIO(${JSON.stringify(inputText || "")})
_out = io.StringIO()
sys.stdout = _out
sys.stderr = _out
try:
${code.split("\n").map((l) => "    " + l).join("\n")}
except Exception as e:
    print(f"Error: {e}")
sys.stdout = sys.__stdout__
sys.stderr = sys.__stderr__
_out.getvalue()
`
    const result = py.runPython(wrapped)
    return result || ""
  } catch (e) {
    return "Error: " + e.message
  }
}

// ── normalise output for comparison ──────────────────────────────────────────

const norm = (s) => (s || "").trim().replace(/\r\n/g, "\n").replace(/\r/g, "\n")

// ── sub-components ────────────────────────────────────────────────────────────

function StepIndicator({ steps, current }) {
  return (
    <div className="flex items-center gap-1">
      {steps.map((s, i) => (
        <React.Fragment key={i}>
          <div className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
            i === current
              ? "bg-yellow-50 text-richblack-900"
              : i < current
              ? "bg-caribbeangreen-300/20 text-caribbeangreen-300"
              : "bg-richblack-700 text-richblack-400"
          }`}>
            {i < current ? <FiCheckCircle size={12} /> : s.icon}
            {s.label}
          </div>
          {i < steps.length - 1 && <div className="h-px w-4 bg-richblack-600" />}
        </React.Fragment>
      ))}
    </div>
  )
}

// ── main component ────────────────────────────────────────────────────────────

export default function QuizLecture({ quizData, subSectionId, courseId, isCompleted }) {
  const dispatch  = useDispatch()
  const { token } = useSelector((s) => s.auth)

  const hasMCQ  = (quizData?.mcqQuestions?.length ?? 0) > 0
  const hasCode = !!(quizData?.codingChallenge?.problem)

  const steps = [
    hasMCQ  && { label: "MCQ",    icon: <FiList size={12} /> },
    hasCode && { label: "Coding", icon: <FiCode size={12} /> },
    { label: "Result", icon: <FiAward size={12} /> },
  ].filter(Boolean)

  const resultStepIdx = steps.length - 1

  const [step,       setStep]       = useState(0)
  const [mcqAnswers, setMcqAnswers] = useState({})   // { [qIdx]: optionIdx }
  const [code,       setCode]       = useState(quizData?.codingChallenge?.starterCode || "")
  const [running,    setRunning]    = useState(false)
  const [testResults, setTestResults] = useState([])  // [{input,expected,got,passed}]
  const [submitting, setSubmitting] = useState(false)
  const [result,     setResult]     = useState(null)  // server response data

  // Load Pyodide eagerly for Python quizzes
  useEffect(() => {
    if (quizData?.codingChallenge?.language === "python" && !window.__kv_pyodide) {
      if (!window.loadPyodide) {
        const s = document.createElement("script")
        s.src = "https://cdn.jsdelivr.net/pyodide/v0.26.4/full/pyodide.js"
        document.head.appendChild(s)
      }
    }
  }, [quizData?.codingChallenge?.language])

  // ── run code against test cases ───────────────────────────────────────────

  const runTests = async () => {
    const cc = quizData.codingChallenge
    if (!cc?.testCases?.length) return []
    setRunning(true)
    const results = []
    for (const tc of cc.testCases) {
      let got = ""
      try {
        if (cc.language === "python") got = await runPython(code, tc.input)
        else                           got = await runJS(code, tc.input)
      } catch (e) { got = "Error: " + e.message }
      results.push({
        input:    tc.input,
        expected: tc.expectedOutput,
        got,
        passed:   norm(got) === norm(tc.expectedOutput),
        label:    tc.label || "",
      })
    }
    setTestResults(results)
    setRunning(false)
    return results
  }

  // ── submit to backend ─────────────────────────────────────────────────────

  const handleSubmit = async (codeResults) => {
    setSubmitting(true)
    const mcqArr = Object.entries(mcqAnswers).map(([qi, sel]) => ({
      questionIndex: Number(qi),
      selected:      sel,
    }))
    const res = await submitInCourseQuiz(
      { subSectionId, courseId, mcqAnswers: mcqArr, codeTestResults: codeResults || [] },
      token
    )
    if (res?.success) {
      setResult(res.data)
      if (res.data.passed) dispatch(updateCompletedLectures(subSectionId))
    }
    setSubmitting(false)
    setStep(resultStepIdx)
  }

  // ── step navigation ───────────────────────────────────────────────────────

  const goNext = async () => {
    const nextStep = step + 1
    const isResultStep = nextStep === resultStepIdx

    if (isResultStep) {
      // If we have code, run tests first then submit; otherwise submit MCQ only
      if (hasCode) {
        const codeRes = await runTests()
        await handleSubmit(codeRes)
      } else {
        await handleSubmit([])
      }
    } else {
      setStep(nextStep)
    }
  }

  // ── render: MCQ step ──────────────────────────────────────────────────────

  if (steps[step]?.label === "MCQ") {
    return (
      <div className="flex flex-col gap-5">
        <StepIndicator steps={steps} current={step} />
        <div className="flex flex-col gap-4">
          {quizData.mcqQuestions.map((q, qi) => (
            <div key={qi} className="rounded-xl border border-richblack-700 bg-richblack-800 p-4">
              <p className="mb-3 text-sm font-semibold text-richblack-5">
                {qi + 1}. {q.question}
              </p>
              <div className="flex flex-col gap-2">
                {q.options.map((opt, oi) => (
                  <label key={oi}
                    className={`flex cursor-pointer items-center gap-3 rounded-lg border px-4 py-2.5 transition-colors ${
                      mcqAnswers[qi] === oi
                        ? "border-yellow-50/50 bg-yellow-50/10 text-yellow-50"
                        : "border-richblack-600 text-richblack-200 hover:border-richblack-500"
                    }`}
                  >
                    <input
                      type="radio"
                      name={`q${qi}`}
                      checked={mcqAnswers[qi] === oi}
                      onChange={() => setMcqAnswers((p) => ({ ...p, [qi]: oi }))}
                      className="accent-yellow-50"
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end">
          <button
            onClick={goNext}
            disabled={Object.keys(mcqAnswers).length < quizData.mcqQuestions.length || submitting}
            className="flex items-center gap-2 rounded-xl bg-yellow-50 px-6 py-2.5 text-sm font-semibold text-richblack-900 transition-all hover:bg-yellow-100 active:scale-95 disabled:opacity-50"
          >
            {hasCode ? "Next: Coding" : submitting ? "Submitting…" : "Submit Quiz"}
            <FiChevronRight size={15} />
          </button>
        </div>
      </div>
    )
  }

  // ── render: Coding step ───────────────────────────────────────────────────

  if (steps[step]?.label === "Coding") {
    const cc = quizData.codingChallenge
    const allPassed = testResults.length > 0 && testResults.every((r) => r.passed)

    return (
      <div className="flex flex-col gap-4">
        <StepIndicator steps={steps} current={step} />

        {/* Problem statement */}
        <div className="rounded-xl border border-richblack-700 bg-richblack-800 p-4">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-richblack-400">
            Problem · {cc.language === "python" ? "Python" : "JavaScript"}
          </p>
          <p className="whitespace-pre-wrap text-sm text-richblack-100">{cc.problem}</p>
        </div>

        {/* Code editor */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold uppercase tracking-wider text-richblack-400">Your Code</label>
          <textarea
            value={code}
            onChange={(e) => setCode(e.target.value)}
            spellCheck={false}
            rows={14}
            className="w-full resize-y rounded-xl border border-richblack-600 bg-richblack-900 p-4 font-mono text-sm text-richblack-5 outline-none focus:border-yellow-50/50"
          />
        </div>

        {/* Run tests */}
        <button
          onClick={runTests}
          disabled={running || !code.trim()}
          className="flex items-center gap-2 self-start rounded-xl border border-richblack-600 bg-richblack-700 px-5 py-2 text-sm font-medium text-richblack-100 transition-all hover:bg-richblack-600 active:scale-95 disabled:opacity-50"
        >
          {running ? <FiLoader size={14} className="animate-spin" /> : <FiPlay size={14} />}
          {running ? "Running…" : "Run Tests"}
        </button>

        {/* Test results */}
        {testResults.length > 0 && (
          <div className="flex flex-col gap-2">
            {testResults.map((r, i) => (
              <div key={i} className={`rounded-xl border p-3 text-xs ${
                r.passed
                  ? "border-caribbeangreen-300/30 bg-caribbeangreen-300/5"
                  : "border-pink-300/30 bg-pink-300/5"
              }`}>
                <div className="mb-1 flex items-center gap-2 font-semibold">
                  {r.passed
                    ? <FiCheckCircle size={13} className="text-caribbeangreen-300" />
                    : <FiXCircle     size={13} className="text-pink-300" />}
                  <span className={r.passed ? "text-caribbeangreen-300" : "text-pink-300"}>
                    Test {i + 1}{r.label ? ` — ${r.label}` : ""}
                  </span>
                </div>
                {r.input && (
                  <div className="mt-1">
                    <span className="text-richblack-400">Input: </span>
                    <code className="text-richblack-200">{r.input}</code>
                  </div>
                )}
                <div>
                  <span className="text-richblack-400">Expected: </span>
                  <code className="text-richblack-200">{r.expected}</code>
                </div>
                {!r.passed && (
                  <div>
                    <span className="text-richblack-400">Got: </span>
                    <code className="text-pink-200">{r.got || "(no output)"}</code>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between gap-3">
          {hasMCQ && (
            <button onClick={() => setStep(step - 1)}
              className="rounded-xl border border-richblack-600 px-5 py-2.5 text-sm font-medium text-richblack-300 hover:text-richblack-100">
              ← Back
            </button>
          )}
          <button
            onClick={goNext}
            disabled={submitting || (testResults.length > 0 && !allPassed && cc.testCases?.length > 0 && testResults.length === 0)}
            className="ml-auto flex items-center gap-2 rounded-xl bg-yellow-50 px-6 py-2.5 text-sm font-semibold text-richblack-900 transition-all hover:bg-yellow-100 active:scale-95 disabled:opacity-50"
          >
            {submitting ? "Submitting…" : "Submit Quiz"}
            <FiChevronRight size={15} />
          </button>
        </div>
      </div>
    )
  }

  // ── render: Result step ───────────────────────────────────────────────────

  if (step === resultStepIdx) {
    if (!result) {
      return (
        <div className="flex min-h-[200px] items-center justify-center">
          <FiLoader size={24} className="animate-spin text-yellow-50" />
        </div>
      )
    }

    const { totalScore, passed, mcqScore, codeScore, mcqCorrect, mcqTotal, passedCases, totalCases } = result

    return (
      <div className="flex flex-col items-center gap-6 py-4 text-center">
        <div className={`flex h-20 w-20 items-center justify-center rounded-full border-4 ${
          passed ? "border-caribbeangreen-300 bg-caribbeangreen-300/10" : "border-pink-300 bg-pink-300/10"
        }`}>
          {passed
            ? <FiCheckCircle size={36} className="text-caribbeangreen-300" />
            : <FiXCircle     size={36} className="text-pink-300" />}
        </div>

        <div>
          <p className={`text-4xl font-extrabold ${passed ? "text-caribbeangreen-300" : "text-pink-300"}`}>
            {totalScore}%
          </p>
          <p className="mt-1 text-sm font-semibold text-richblack-200">
            {passed ? "Quiz Passed! Lecture marked complete." : `Need ${quizData.passingScore ?? 70}% to pass. Try again.`}
          </p>
        </div>

        {/* Score breakdown */}
        <div className="flex gap-6">
          {hasMCQ && (
            <div className="rounded-xl border border-richblack-700 bg-richblack-800 px-5 py-3 text-center">
              <p className="text-xs text-richblack-400">MCQ</p>
              <p className="text-xl font-bold text-richblack-5">{mcqScore}%</p>
              <p className="text-xs text-richblack-500">{mcqCorrect}/{mcqTotal} correct</p>
            </div>
          )}
          {hasCode && (
            <div className="rounded-xl border border-richblack-700 bg-richblack-800 px-5 py-3 text-center">
              <p className="text-xs text-richblack-400">Coding</p>
              <p className="text-xl font-bold text-richblack-5">{codeScore}%</p>
              <p className="text-xs text-richblack-500">{passedCases}/{totalCases} tests</p>
            </div>
          )}
        </div>

        {!passed && (
          <button
            onClick={() => {
              setStep(0)
              setMcqAnswers({})
              setCode(quizData?.codingChallenge?.starterCode || "")
              setTestResults([])
              setResult(null)
            }}
            className="rounded-xl border border-richblack-600 bg-richblack-700 px-6 py-2.5 text-sm font-medium text-richblack-100 transition-all hover:bg-richblack-600"
          >
            Retry Quiz
          </button>
        )}
      </div>
    )
  }

  return null
}
